import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import LocalLibraryOutlinedIcon from "@mui/icons-material/LocalLibraryOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import StarOutlineRoundedIcon from "@mui/icons-material/StarOutlineRounded";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import { type FC, type ReactElement, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import { type ShiftTrainingFlag, useShiftFlagActions } from "../../../../hooks/useShiftFlagActions";
import { type NurseInShift, NurseTrainingStatus } from "../../../../utils/models";
import { getShiftIndexByShiftTimes } from "../../../../utils/utilFunctions";
import { SheetActionRow } from "../../../UIToolkit/Clickable/SheetActionRow";
import { SheetStep } from "../../../UIToolkit/Popups/SheetStep";
import { SheetStepButton } from "../../../UIToolkit/Popups/SheetStepButton";
import { GapsSavingFlag, GapsStaffStep } from "./gapsSheetTypes";

type Props = {
	nurseInShift: NurseInShift;
	/** A punched shift on a past day: the toggles write the actual shift. */
	isActual: boolean;
	/** One of a punched day's Actual-table rows (`isActualTableEntry`): History is not offered, as on desktop. */
	isActualTableRow: boolean;
	name: string;
	/**
	 * The toggle whose write is in flight for this shift, or `null`. Owned by the sheet, not this step, so it outlives
	 * the step: leave More mid-save and come back, and that row still shows progress while the others wait.
	 */
	saving: GapsSavingFlag | null;
	onSavingChange: (shiftId: string, flag: GapsSavingFlag, isSaving: boolean) => void;
	onStep: (step: GapsStaffStep) => void;
};

const SHEET = "gaps-staff-more";

const TRAINING_ROWS: { flag: ShiftTrainingFlag; saving: GapsSavingFlag; labelKey: string; icon: ReactElement }[] = [
	{ flag: NurseTrainingStatus.Trainee, saving: GapsSavingFlag.Trainee, labelKey: "gaps.staffSheet.trainee", icon: <LocalLibraryOutlinedIcon /> },
	{ flag: NurseTrainingStatus.Orientation, saving: GapsSavingFlag.Orientation, labelKey: "gaps.staffSheet.orientation", icon: <SchoolOutlinedIcon /> },
	{ flag: NurseTrainingStatus.Education, saving: GapsSavingFlag.Education, labelKey: "gaps.staffSheet.education", icon: <MenuBookOutlinedIcon /> },
];

/**
 * The quieter things about a person on a shift (P3-5): the note, charge, and the three training statuses.
 *
 * Each toggle saves as it is tapped — no confirm — through `useShiftFlagActions`, the desktop nurse menu's own
 * logic: the same write, the same local update, the same events. The statuses are mutually exclusive, and tapping
 * the one that is on clears it back to Regular, exactly as the menu does. None of the three counts toward par; the
 * sheet says so on each row, because the change a head nurse sees in the group's `filled` right after is the point.
 */
export const GapsStaffMoreStep: FC<Props> = ({ nurseInShift, isActual, isActualTableRow, name, saving, onSavingChange, onStep }) => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { chosenDepartment, getWeekIndexByDateTime, getDayIndexByDateTime } = useHospitalData();

	// The shift's coordinates, resolved from its own times exactly as `NormalizedNurseMenu` hands them to the menu.
	const { toggleCharge, toggleTrainingStatus } = useShiftFlagActions({
		nurseInShift,
		weekIndex: getWeekIndexByDateTime(nurseInShift.startTime),
		shiftNo: getShiftIndexByShiftTimes({ startTime: nurseInShift.startTime, endTime: nurseInShift.endTime }),
		dayNo: getDayIndexByDateTime(nurseInShift.startTime),
		showActual: isActual,
		analyticsSource: "gaps_staff_sheet",
	});

	const runToggle = useCallback(
		async (flag: GapsSavingFlag, turningOn: boolean, write: () => Promise<boolean>): Promise<void> => {
			const properties = { department_id: chosenDepartment.department_id, shift_id: nurseInShift.id, flag, turning_on: turningOn };
			track("gaps_staff_flag_toggle_start", properties);
			onSavingChange(nurseInShift.id, flag, true);
			// `false` is a failed write (already in the error snackbar) — a skipped one cannot happen here, because every
			// row ignores taps while a toggle for this shift is in flight, including after leaving the step and coming back.
			const written = await write();
			onSavingChange(nurseInShift.id, flag, false);
			if (written) track("gaps_staff_flag_toggle_success", properties);
			else trackError("gaps_staff_flag_toggle_error", properties);
		},
		[chosenDepartment.department_id, nurseInShift.id, onSavingChange, track, trackError],
	);

	const isBusy = saving !== null;

	return (
		<SheetStep
			stepKey={GapsStaffStep.More}
			dataTestId={SHEET}
			title={t("gaps.staffSheet.moreTitle", { name })}
			subtitle={t("gaps.staffSheet.moreSubtitle")}
			footer={
				<SheetStepButton tone="dismiss" onClick={() => onStep(GapsStaffStep.Actions)} dataTestId={`${SHEET}-cancel`}>
					{t("back")}
				</SheetStepButton>
			}
		>
			<SheetActionRow
				icon={<StickyNote2OutlinedIcon />}
				label={nurseInShift.notes ? t("gaps.staffSheet.editNote") : t("gaps.staffSheet.addNote")}
				// An empty note is no note: the row shows no second line rather than a blank one.
				description={nurseInShift.notes ? nurseInShift.notes : undefined}
				showChevron
				disabled={isBusy}
				onClick={() => onStep(GapsStaffStep.Note)}
				dataTestId={`${SHEET}-note`}
			/>
			<SheetActionRow
				kind="switch"
				checked={nurseInShift.charge}
				icon={<StarOutlineRoundedIcon />}
				label={t("gaps.staffSheet.charge")}
				loading={saving === GapsSavingFlag.Charge}
				disabled={isBusy && saving !== GapsSavingFlag.Charge}
				onClick={() => void runToggle(GapsSavingFlag.Charge, !nurseInShift.charge, toggleCharge)}
				dataTestId={`${SHEET}-charge`}
			/>
			{TRAINING_ROWS.map(({ flag, saving: rowFlag, labelKey, icon }) => {
				const isOn = nurseInShift.trainingStatus === flag;
				return (
					<SheetActionRow
						key={flag}
						kind="switch"
						checked={isOn}
						icon={icon}
						label={t(labelKey)}
						description={t("gaps.staffSheet.notCounted")}
						loading={saving === rowFlag}
						disabled={isBusy && saving !== rowFlag}
						onClick={() => void runToggle(rowFlag, !isOn, () => toggleTrainingStatus(flag))}
						dataTestId={`${SHEET}-${rowFlag}`}
					/>
				);
			})}
			{/* The desktop nurse menu hides History for a worked shift too: punches have their own sync history. */}
			{!isActualTableRow && (
				<SheetActionRow
					icon={<HistoryRoundedIcon />}
					label={t("shiftHistory.menu")}
					description={t("shiftHistory.sheetRowDescription")}
					showChevron
					disabled={isBusy}
					onClick={() => onStep(GapsStaffStep.History)}
					dataTestId={`${SHEET}-history`}
				/>
			)}
		</SheetStep>
	);
};
